import { firstValueFrom, Observable, of, Subject as RxjsSubject, throwError, zip } from "rxjs";
import { filter, switchMap, takeUntil, tap } from "rxjs/operators";
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Injectable, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormControl, ValidatorFn, Validators } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { platform } from "../../../../platform/platform";
import { ApiError } from "../../../_errors/api-error";
import { ApplicationError } from "../../../_errors/application-error";
import { Inspection, InspectionDetailNew, InspectionDetailUpdate, Inspector } from "../../../_objects/inspection";
import { Subject } from "../../../_objects/subject";
import { SubjectLocation, SubjectModel, SubjectModelListItem } from "../../../_objects/subject-model";
import { ConfigurationItem } from "../../../_objects/tenant-config";
import { TenantUserList } from "../../../_objects/user";
import { IplexDeviceConnectModeService } from "../../../_offline/services/iplex-device-connect-mode.service";
import { UtilsService } from "../../../_services/_utils.service";
import { CommonSnackBarService } from "../../../_services/common-snack-bar.service";
import { InspectionService } from "../../../_services/http-api/inspection.service";
import { LoginUserOnlineService } from "../../../_services/http-api/login-user-online.service";
import { StructureService } from "../../../_services/http-api/structure.service";
import { SubjectService } from "../../../_services/http-api/subject.service";
import { TenantConfigService } from "../../../_services/http-api/tenant-config.service";
import { LangUpdateService } from "../../../_services/lang-update.service";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { LoginUserOfflineService } from "../../../_services/offline/login-user-offline.service";
import { SpinnerService } from "../../../_services/spinner.service";
import { CustomValidators } from "../../../_shared/custom-validator";
import { DATE_PICKER_VALIDATION, MODE, MODEL_CODE, PERMISSION_CODE, PLATFORM, REGULAR_EXPRESSION, TenantConfig } from "../../../app.constants";
import { CommonConfirmDialogComponent } from "../../../common-dialog/common-confirm-dialog.component";
import { CommonDeleteDialogComponent } from "../../../common-dialog/common-delete-dialog.component";
import { SettingsSubjectEditDialogComponent } from "../../../settings/settings-subject/subject-edit-dialog/settings-subject-edit-dialog.component";
import { IplexDeviceSyncService } from "../../../_offline/services/iplex-device-sync.service";
import { TitleDbService } from "../../../_services/database/title-db.service";
import { FileSystemAccessService } from "../../../_services/file-system-access.service";
import { OfflineUtilsService } from "../../../_offline/services/offline-utils.service";
import { SubjectModelDbService } from "../../../_services/database/subject-model-db.service";
import { SubjectModelSchema } from "../../../_offline/models/subjectModel.schema";
import { FILE_SYSTEM, OFFLINE_REGEX } from "../../../_offline/electron.constants";
import { CommonFolderInUseDialogComponent } from "../../../common-dialog/common-folder-in-use-dialog/common-folder-in-use-dialog.component";
import { UsersStateService } from "../../../_services/user-state/users-state.service";
import { InspectionTemplateDownloadService } from "../../../_services/inspection-template-download.service";
import { mode } from "../../../../mode/mode";
import { MarksStateService } from "../../../_services/mark-state/marks-state.service";
import { RoutePage } from "../../../app-routes.enum";

/**
 * To display the date as yyyy/MM/dd,
 * the format() of NativeDateAdapter is overridden.
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  format(date: Date, displayFormat: string): string {
    if (displayFormat === "dateInput") {
      // Format 'dateInput' to yyyy/MM/dd.
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return year + "/" + this._to2digit(month) + "/" + this._to2digit(day);
    } else {
      // Anything other than 'dateInput' will behave like NativeDateAdapter.
      return super.format(date, displayFormat);
    }
  }

  private _to2digit(n: number) {
    return ("00" + n).slice(-2);
  }
}

/**
 * Since we only want to change the behavior of dateInput,
 * leave everything except dateInput as MAT_NATIVE_DATE_FORMATS.
 */
export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null
  },
  display: {
    dateInput: "dateInput",
    monthYearLabel: { year: "numeric", month: "short" },
    dateA11yLabel: { year: "numeric", month: "long", day: "numeric" },
    monthYearA11yLabel: { year: "numeric", month: "long" }
  }
};

export class InspectorWrapper {
  inspector: Inspector;
  removed: boolean;
  order: number;
  name: string;

  constructor(inspector: Inspector) {
    this.inspector = inspector;
  }
}

export class SubjectWrapper {
  subject: Subject;
  removed: boolean;
  attributes: string[];

  constructor(subject: Subject, configs: ConfigurationItem[]) {
    this.subject = subject;
    this.attributes = [];
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeOwner.KEY)?.value as boolean) &&
      this.subject.owner !== "" &&
      this.subject.owner !== null
    ) {
      this.attributes.push(this.subject.owner);
    }
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeCountry.KEY)?.value as boolean) &&
      this.subject.country !== "" &&
      this.subject.country !== null
    ) {
      this.attributes.push(this.subject.country);
    }
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeState.KEY)?.value as boolean) &&
      this.subject.state !== "" &&
      this.subject.state !== null
    ) {
      this.attributes.push(this.subject.state);
    }
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeDistrict.KEY)?.value as boolean) &&
      this.subject.district !== "" &&
      this.subject.district !== null
    ) {
      this.attributes.push(this.subject.district);
    }
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeSite.KEY)?.value as boolean) &&
      this.subject.site !== "" &&
      this.subject.site !== null
    ) {
      this.attributes.push(this.subject.site);
    }
    if (
      (configs.find((_) => _.key === TenantConfig.SubjectAttributeProject.KEY)?.value as boolean) &&
      this.subject.project !== "" &&
      this.subject.project !== null
    ) {
      this.attributes.push(this.subject.project);
    }
  }

  get etcAttributesLabel() {
    return this.attributes.filter((_, i) => i > 1).join(", ");
  }
}

@Component({
  selector: "app-inspection-upsert-dialog",
  templateUrl: "./inspection-upsert-dialog.component.html",
  styleUrls: ["./inspection-upsert-dialog.component.scss"],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  standalone: false
})

/**
 * @Deprecated Remove after Asset Module Integration
 */
export class InspectionUpsertDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly minDate = new Date(DATE_PICKER_VALIDATION.MIN_YEAR, DATE_PICKER_VALIDATION.MIN_MONTH - 1, DATE_PICKER_VALIDATION.MIN_DAY);
  readonly maxDate = new Date(DATE_PICKER_VALIDATION.MAX_YEAR, DATE_PICKER_VALIDATION.MAX_MONTH - 1, DATE_PICKER_VALIDATION.MAX_DAY);
  readonly inspectionTitleMaxLength = 100;
  readonly modelCode = MODEL_CODE.IPLEXNX;
  // Management
  inspection: Inspection;
  inspectionName = "";
  status: string;
  invalidLocationFolderNameList: string[] = [];
  groupedSubjects: { [modelId: string]: SubjectWrapper[] } = {};

  // Master/Choice
  inspectorsMaster: InspectorWrapper[] = [];
  selectableInspectors: InspectorWrapper[] = [];
  subjects: SubjectWrapper[] = [];
  filteredSubjects: SubjectWrapper[];
  subjectModels: SubjectModelListItem[];
  dowloadingStructure = false;

  // Forms
  inspectionTitleForm = new FormControl<string>({ value: "", disabled: true }, [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.inspectionTitleMaxLength),
    this.regexValidator()
  ]);
  subjectForm = new FormControl<SubjectWrapper>({ value: null, disabled: true }, [CustomValidators.requiredTrimmed()]);
  inspectionDateForm = new FormControl<string>({ value: "", disabled: true }, [CustomValidators.requiredTrimmed()]);
  inspectorsForm = new FormControl<InspectorWrapper[]>(
    {
      value: null,
      disabled: true
    },
    [CustomValidators.requiredTrimmed()]
  );

  // Flags
  isProcess = false;
  isEditableName = false;
  isUpdateMode = false;

  // Labels
  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;

  ownerLabel = TenantConfig.DefaultConfigurationValues.LabelOfOwner;
  countryLabel = TenantConfig.DefaultConfigurationValues.LabelOfCountry;
  stateLabel = TenantConfig.DefaultConfigurationValues.LabelOfState;
  districtLabel = TenantConfig.DefaultConfigurationValues.LabelOfRegion;
  siteLabel = TenantConfig.DefaultConfigurationValues.LabelOfSite;
  projectLabel = TenantConfig.DefaultConfigurationValues.LabelOfProject;
  serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;

  // S/N attribute display flag
  isShowOwner = false;
  isShowCountry = false;
  isShowState = false;
  isShowDistrict = false;
  isShowSite = false;
  isShowProject = false;

  visibleAttrExists = false;

  readonly regularExpression = this.utilsService.CombiningRegExp([
    "^",
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    "[",
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    "]+$"
  ]);

  readonly regularExpressionStr = "a-z A-Z 0-9 !#$%&'()+\\-;=@\\[\\]^`{}~_,.";
  @Output() inserted = new EventEmitter<Inspection>();
  @Output() updateSubjects = new EventEmitter<Subject[]>();
  @Output() changedInspection = new EventEmitter<Inspection>();
  @ViewChild("inspectors") inspectorsSelect: MatSelect;
  @ViewChild("searchInput") searchInput: ElementRef;
  private onDestroy$ = new RxjsSubject();
  private folderIndex = 1;
  private newInspectionTitleUpdatedByUser = false;

  constructor(
    private router: Router,
    private inspectionService: InspectionService,
    private subjectService: SubjectService,
    private tenantConfigService: TenantConfigService,
    private tenantUserService: UsersStateService,
    private dialog: MatDialog,
    private snackBar: CommonSnackBarService,
    private utilsService: UtilsService,
    private loginUserService: LoginUserOnlineService,
    private loginUserOfflineService: LoginUserOfflineService,
    private structureService: StructureService,
    private apiError: ApiError,
    private dialogRef: MatDialogRef<InspectionUpsertDialogComponent>,
    private oscSpinnerService: SpinnerService,
    private networkStatusService: NetworkStatusService,
    private deviceConnectModeService: IplexDeviceConnectModeService,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private offlineUtils: OfflineUtilsService,
    private fileSystemAccessService: FileSystemAccessService,
    private titleDBService: TitleDbService,
    private marksStateService: MarksStateService,
    private subjectModelDbService: SubjectModelDbService,
    private iplexDeviceSyncService: IplexDeviceSyncService,
    private inspectionTemplateDownloadService: InspectionTemplateDownloadService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    if (this.data?.inspection) {
      this.inspection = this.data.inspection;
      this.isUpdateMode = true;
    }

    if (this.data?.subjects) {
      this.subjects = this.createSubjectWrapper(this.data.subjects);
      this.filteredSubjects = this.subjects.filter(() => true);
    }

    if (this.data?.subjectModels) {
      this.subjectModels = this.data.subjectModels.sort((subjectModel1, subjectModel2) => {
        if (subjectModel1.name < subjectModel2.name) {
          return -1;
        }
        if (subjectModel1.name > subjectModel2.name) {
          return 1;
        }
        return 0;
      });
    }
    this.updateGroupedSubjects();

    this.oscSpinnerService.show(true);
    this.tenantUserService.users$
      .pipe(tap((users) => (users ? this.oscSpinnerService.hide(true) : {})))
      .pipe(filter((users) => users.length != 0))
      .subscribe({
        next: (data) => {
          const loginUser =
            platform.name === PLATFORM.DESKTOP && !this.networkStatusService.isOnline
              ? this.loginUserOfflineService.loginUser
              : this.loginUserService.loginUser;
          this.inspectorsMaster = data
            .map((userItem) => this.userSelectItem2Inspector(userItem))
            .sort((inspector1, inspector2) => {
              if (loginUser) {
                if (inspector1.inspector.id === loginUser.id) {
                  return -1;
                }
                if (inspector2.inspector.id === loginUser.id) {
                  return 1;
                }
              }
              if (inspector1.name < inspector2.name) {
                return -1;
              }
              if (inspector1.name > inspector2.name) {
                return 1;
              }
              return 0;
            });

          this.selectableInspectors = !this.isUpdateMode
            ? this.inspectorsMaster.filter((master) => master.inspector.isActive && master.inspector.isSubscription)
            : this.inspectorsMaster;

          if (!this.isReadOnly()) {
            this.inspectorsForm.enable();
          }

          let selected: InspectorWrapper[] = [];
          if (this.inspection) {
            selected = this.initInspectors(this.inspectorsMaster, this.inspection.inspectors);
            this.selectableInspectors = this.selectableInspectors.filter(
              (_) => (_.inspector.isActive && _.inspector.isSubscription) || selected.some((s) => s.inspector.id == _.inspector.id)
            );
          } else {
            const setUser = this.inspectorsMaster.find((wrap) => wrap.inspector.id === loginUser.id);
            selected = [setUser];
          }
          this.oscSpinnerService.hide(true);
          this.inspectorsForm.setValue(selected);
          if (!this.newInspectionTitleUpdatedByUser) this.loadInspectionName();
        },
        error: (error) => {
          this.oscSpinnerService.hide(true);
          console.error(error);
          if (!this.inspection) {
            if (!this.isReadOnly()) {
              this.inspectorsForm.enable();
            }
          }
        }
      });
  }

  get lowerCaseMarkLabel(): string {
    return this.markLabel.toLowerCase();
  }

  get lowerCaseTitleLabel(): string {
    return this.titleLabel.toLowerCase();
  }

  get isDeviceConnectMode() {
    return this.isOffline && this.deviceConnectModeService.isHttpsConnectMode;
  }

  get isWeb() {
    return platform.name === PLATFORM.WEB;
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOnPremise() {
    return this.networkStatusService.isOnPremise;
  }

  get isBrianMode() {
    return this.networkStatusService.isBrianMode;
  }

  get isContainInvalidCharInSN() {
    // When serialNo contains unexpected characters and the Auto-fill checkbox is checked
    return (
      this.subjectForm.value && this.isEditableName && !this.isUpdateMode && !this.regularExpression.test(this.subjectForm.value.subject.serialNo)
    );
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  ngOnInit() {
    this.setConfig();
    this.inspection ? this.setInspection(this.inspection) : this.initForm(null, this.inspectorsMaster, this.subjects);
  }

  ngAfterViewInit(): void {
    this.inspectorsSelect?.selectionChange.pipe(takeUntil(this.onDestroy$)).subscribe((matSelectChange) => {
      this.reorder(matSelectChange.value, this.inspectorsMaster);
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  setInspection(insp: Inspection) {
    /* If the Subject or Inspector included in the Inspection has been removed from the master, add it. */
    this.addSubjectIfNeed(this.subjects, insp);
    this.addInspectorIfNeed(this.inspectorsMaster, insp);

    // Initializing a Form
    this.initForm(insp, this.inspectorsMaster, this.subjects);
  }

  setConfig() {
    this.visibleAttrExists = this.tenantConfigService.visibleAttrExists;

    const configs = this.tenantConfigService.tenantConfig;

    if (configs) {
      this.markLabel = this.getConfigValue(configs, TenantConfig.LabelOfMark.KEY, "");
      this.titleLabel = this.getConfigValue(configs, TenantConfig.LabelOfTitle.KEY, "");
      this.ownerLabel = this.getConfigValue(configs, TenantConfig.LabelOfOwner.KEY, "");
      this.countryLabel = this.getConfigValue(configs, TenantConfig.LabelOfCountry.KEY, "");
      this.stateLabel = this.getConfigValue(configs, TenantConfig.LabelOfState.KEY, "");
      this.districtLabel = this.getConfigValue(configs, TenantConfig.LabelOfRegion.KEY, "");
      this.siteLabel = this.getConfigValue(configs, TenantConfig.LabelOfSite.KEY, "");
      this.projectLabel = this.getConfigValue(configs, TenantConfig.LabelOfProject.KEY, "");

      this.isShowOwner = this.getConfigValue(configs, TenantConfig.SubjectAttributeOwner.KEY, false) as boolean;
      this.isShowCountry = this.getConfigValue(configs, TenantConfig.SubjectAttributeCountry.KEY, false) as boolean;
      this.isShowState = this.getConfigValue(configs, TenantConfig.SubjectAttributeState.KEY, false) as boolean;
      this.isShowDistrict = this.getConfigValue(configs, TenantConfig.SubjectAttributeDistrict.KEY, false) as boolean;
      this.isShowSite = this.getConfigValue(configs, TenantConfig.SubjectAttributeSite.KEY, false) as boolean;
      this.isShowProject = this.getConfigValue(configs, TenantConfig.SubjectAttributeProject.KEY, false) as boolean;

      this.serialNumberLabel = this.getConfigValue(configs, TenantConfig.LabelOfSerialNumber.KEY, "");
    }
  }

  private getConfigValue(configs: any[], key: string, defaultValue: any = null) {
    const configItem = configs.find((_) => _.key === key);
    return configItem ? configItem.value : defaultValue;
  }

  createSubjectWrapper(subjects: Subject[]): SubjectWrapper[] {
    return subjects
      .sort((subject1, subject2) => {
        if (subject1.serialNo < subject2.serialNo) {
          return -1;
        }
        if (subject1.serialNo > subject2.serialNo) {
          return 1;
        }
        return 0;
      })
      .map((subject) => new SubjectWrapper(subject, this.tenantConfigService.tenantConfig));
  }

  reorder(selected: InspectorWrapper[], inspectorsMaster: InspectorWrapper[]) {
    // It is not assumed that select and deselsect will occur at the same time, or that two will be selected at the same time.

    let added: InspectorWrapper;
    let maxOrder = 0;
    inspectorsMaster.forEach((master) => {
      // What's deselected
      if (selected.indexOf(master) < 0) {
        master.order = null;
      }

      // What was selected
      if (selected.indexOf(master) >= 0 && (master.order === null || master.order === undefined)) {
        added = master;
      }

      if (maxOrder < master.order) {
        maxOrder = master.order;
      }
    });

    if (added) {
      // Count the selected items
      added.order = maxOrder + 1;
    }
  }

  addInspectorIfNeed(inspectorsMaster: InspectorWrapper[], inspection: Inspection) {
    if (!inspectorsMaster) {
      throw new ApplicationError("Inspector Master is invalid.");
    }
    if (!inspection || !inspection.inspectors || inspection.inspectors.length <= 0) {
      throw new ApplicationError("Inspection is invalid.");
    }

    const deletedInspectors: InspectorWrapper[] = [];
    inspection.inspectors.forEach((inspector) => {
      if (!inspector.id) {
        throw new ApplicationError("Inspection.Inspector is invalid.");
      }

      const exist = inspectorsMaster.some((master) => master.inspector.id === inspector.id);
      if (!exist) {
        const dst = new InspectorWrapper(new Inspector());
        dst.inspector.id = inspector.id;
        dst.removed = true;
        deletedInspectors.push(dst);
      }
    });

    if (deletedInspectors.length > 0) {
      Array.prototype.push.apply(inspectorsMaster, deletedInspectors);
    }
  }

  addSubjectIfNeed(subjects: SubjectWrapper[], inspection: Inspection) {
    if (!subjects) {
      throw new ApplicationError("Subjects is invalid.");
    }
    if (!inspection || !inspection.subjectId || !inspection.subjectModelId || !inspection.subjectSerialNo) {
      throw new ApplicationError("Inspection is invalid.");
    }

    const exist = subjects.some((subject) => subject.subject.id === inspection.subjectId);

    if (!exist) {
      const subject = new SubjectWrapper(new Subject(), this.tenantConfigService.tenantConfig);
      subject.subject.id = inspection.subjectId;
      subject.subject.subjectModelId = inspection.subjectModelId;
      subject.subject.serialNo = inspection.subjectSerialNo;
      subject.removed = true;
      subjects.push(subject);
    }
  }

  initForm(inspection: Inspection, inspectorsMaster: InspectorWrapper[], subjects: SubjectWrapper[]): void {
    // create new inspection dialog
    if (!inspection) {
      if (!this.isReadOnly()) {
        this.subjectForm.enable();
        this.inspectionTitleForm.enable();
        this.inspectionDateForm.enable();
      }
      if (inspectorsMaster[0]) {
        const meSelected: InspectorWrapper[] = [];
        meSelected.push(inspectorsMaster[0]);
        this.inspectorsForm.setValue(meSelected);
      }
      this.inspectionDateForm.setValue(new Date().toISOString());
      if (!this.newInspectionTitleUpdatedByUser) this.loadInspectionName();
    } // open inspection info dialog
    else {
      this.inspectionName = this.inspection.title;
      this.status = inspection.status;
      this.inspectionTitleForm.setValue(inspection.title);

      // The initial display shows the Attributes when the Inspection was created.
      const subjectId = subjects.find((s) => s.subject.id === inspection.subjectId).subject.id;
      this.subjectService.getSubject(subjectId, inspection.attributesVersion).subscribe((sb) => {
        const sbs = this.createSubjectWrapper([sb]);
        this.subjectForm.setValue(sbs[0]);
      });
      if (!this.isReadOnly()) {
        if (this.status === "000") {
          this.subjectForm.enable();
        }
        this.inspectionTitleForm.enable();
        this.inspectionDateForm.enable();
      }
      this.inspectionDateForm.setValue(inspection.inspectionDate);
      if (this.data?.inspectionImageCount != 0) {
        this.subjectForm.disable();
      }
      const selected = this.initInspectors(inspectorsMaster, inspection.inspectors);
      this.inspectorsForm.setValue(selected);
    }
  }

  initInspectors(masters: InspectorWrapper[], selection: Inspector[]): InspectorWrapper[] {
    if (!masters || !selection) {
      return [];
    }

    const ids: string[] = selection.map((inspector) => inspector.id);

    const selected: InspectorWrapper[] = [];
    ids.forEach((id, index) => {
      masters.forEach((master) => {
        if (id === master.inspector.id) {
          selected.push(master);
          master.order = index;
        }
      });
    });
    return selected;
  }

  submit() {
    this.isUpdateMode ? this.update() : this.insert();
  }

  toDateString(localDateString: string): string {
    const theDate = new Date(localDateString);

    const year = theDate.getFullYear().toString();
    const month = ("0" + (theDate.getMonth() + 1)).slice(-2); // 0埋め
    const date = ("0" + theDate.getDate()).slice(-2); // 0埋め
    return year + "-" + month + "-" + date + "T00:00:00";
  }

  getSortedInspectors(): InspectorWrapper[] {
    return this.inspectorsForm.value.slice().sort((a, b) => a.order - b.order);
  }

  async update() {
    this.oscSpinnerService.show(true);
    let locationVersion: number;
    const subjectFormValue = this.subjectForm.value as SubjectWrapper;
    const targetSubjectModel = this.subjectModels.find((_) => _.id === subjectFormValue.subject.subjectModelId);
    if (targetSubjectModel && targetSubjectModel.id && this.inspection.subjectModelId === targetSubjectModel.id) {
      // If a higher version exists for the specimen being used, check for a version update.
      if (targetSubjectModel.version > this.inspection.locationVersion) {
        this.oscSpinnerService.hide(true);
        const dialogRef = this.openLocationVersionConfirmDialog();
        locationVersion = await dialogRef
          .afterClosed()
          .toPromise()
          .then((isUseNewestVersion) => (isUseNewestVersion ? targetSubjectModel.version : this.inspection.locationVersion));
        this.oscSpinnerService.show(true);
      }
    } else if (!targetSubjectModel) {
      locationVersion = this.inspection.locationVersion;
    } else {
      locationVersion = targetSubjectModel.version;
    }

    const detailUpdate: InspectionDetailUpdate = this.createInspectionDetailUpdate(
      this.inspectionTitleForm.value,
      this.subjectForm.value,
      this.toDateString(this.inspectionDateForm.value),
      this.getSortedInspectors(),
      this.inspection,
      locationVersion
    );

    this.inspectionService.updateInspection(detailUpdate).subscribe(
      (inspection) => {
        this.changedInspection.emit(inspection);
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.updateSuccessfully"));
        this.utilsService.updateHeaderName();

        this.oscSpinnerService.hide(true);
      },
      (error) => {
        this.oscSpinnerService.hide(true);
        console.log(error);
        if (error.message === "Error: EPERM: operation not permitted") {
          this.openFolderInUseDialog();
        } else {
          this.snackBar.open(this.translate.instant("inspectionUpsertDialog.updateFailed"));
        }
      }
    );
  }

  updateGroupedSubjects() {
    this.groupedSubjects = {};
    if (!this.filteredSubjects) return;

    for (const filteredSubject of this.filteredSubjects) {
      const id = filteredSubject.subject.subjectModelId;

      if (!this.groupedSubjects[id]) {
        this.groupedSubjects[id] = [];
      }

      this.groupedSubjects[id].push(filteredSubject);
    }
  }

  trackBySubjectModel(index: number, model: SubjectModel) {
    return model.id;
  }

  trackBySubjectWrapper(index: number, wrapper: SubjectWrapper) {
    return wrapper.subject.id; // or any unique ID
  }

  insert() {
    this.oscSpinnerService.show(true);
    this.isProcess = true;
    const detailNew: InspectionDetailNew = this.createInspectionDetailNew(
      this.inspectionTitleForm.value,
      this.subjectForm.value,
      this.toDateString(this.inspectionDateForm.value),
      this.getSortedInspectors()
    );

    this.inspectionService.addInspection(detailNew).subscribe(
      (inspection) => {
        this.inserted.emit(inspection);
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.insertSuccessfully"));
      },
      (error) => {
        console.log(error);
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.insertFailed"));
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
      },
      () => {
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
      }
    );
  }

  delete(): void {
    if (!this.inspection || !this.inspection.id || !this.inspection.title) {
      throw new ApplicationError("Inspection is invalid.");
    }

    this.openDeleteDialog(this.translate.instant("inspectionUpsertDialog.inspection"), [this.inspection.title]).subscribe((result) => {
      this.deleteSub(result);
    });
  }

  deleteSub(result: boolean): void {
    if (!result) {
      return;
    }

    this.oscSpinnerService.show(true);
    this.inspectionService.deleteInspection(this.inspection.id).subscribe(
      (_) => this.router.navigate([RoutePage.InspectionList]),
      (error) => console.log(error),
      () => {
        this.dialogRef.close();
        this.oscSpinnerService.hide(true);
      }
    );
  }

  openDeleteDialog(target: string, titles: string[]): Observable<any> {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: "600px",
      minWidth: "400px",
      maxHeight: "800px",
      data: { target: target, deleteNames: titles }
    });
    return dialogRef.afterClosed();
  }

  createInspectionDetailUpdate(
    title: string,
    subject: SubjectWrapper,
    inspectionDate: string,
    inspectors: InspectorWrapper[],
    inspection: Inspection,
    locationVersion: number
  ): InspectionDetailUpdate {
    const inspectionDetailUpdate: InspectionDetailUpdate = new InspectionDetailUpdate();

    this.createInspectionDetailNew(title, subject, inspectionDate, inspectors, inspectionDetailUpdate);

    inspectionDetailUpdate.id = inspection.id;
    inspectionDetailUpdate.schemaVersion = inspection.schemaVersion;
    inspectionDetailUpdate.status = inspection.status;
    inspectionDetailUpdate.locationVersion = locationVersion;

    return inspectionDetailUpdate;
  }

  createInspectionDetailNew(
    title: string,
    subject: SubjectWrapper,
    inspectionDate: string,
    inspectors: InspectorWrapper[],
    dst?: InspectionDetailNew
  ): InspectionDetailNew {
    if (!dst) {
      dst = new InspectionDetailNew();
    }

    dst.title = title.trim();
    dst.subjectId = subject.subject.id;
    dst.inspectionDate = inspectionDate;

    dst.inspectors = inspectors.map((wrapper) => wrapper.inspector);
    dst.status = "000";
    dst.schemaVersion = "1";

    return dst;
  }

  userSelectItem2Inspector(item: TenantUserList): InspectorWrapper {
    if (!item || !item.id) {
      throw new ApplicationError("UserSelectItem is invalid.");
    }

    const inspector = new InspectorWrapper(item);
    inspector.inspector.id = item.id;
    inspector.name = item.givenName + " " + item.surname;

    return inspector;
  }

  createSubject(): void {
    this.oscSpinnerService.show();

    const dialogRef = this.openCreateSubjectDialog();
    this.oscSpinnerService.hide();
    dialogRef.subscribe(
      (createdSubject) => {
        if (createdSubject) {
          this.getSubjects(createdSubject.id);
        } else {
          this.oscSpinnerService.hide();
        }
      },
      (error) => {
        console.log(error);
        this.oscSpinnerService.hide();
      }
    );
  }

  onClickEditAttributes(): void {
    const subjectModelId = this.subjectForm.value.subject.subjectModelId;
    const _subject = this.subjectForm.value.subject;
    this.oscSpinnerService.show();
    this.subjectService.getSubject(_subject.id).subscribe(
      (subject) => {
        this.oscSpinnerService.hide();
        const dialogRef = this.dialog.open(SettingsSubjectEditDialogComponent, {
          width: "524px",
          maxHeight: "95%",
          autoFocus: false,
          data: { subjectModelId, subject, isEditAttrOnly: true }
        });

        dialogRef.afterClosed().subscribe(
          (updatedSubject) => (updatedSubject ? this.getSubjects(updatedSubject.id) : this.oscSpinnerService.hide()),
          (error) => {
            console.log(error);
            this.oscSpinnerService.hide();
          }
        );
      },
      (err) => {
        console.log(err);
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.getSubjectFailed", { serialNumberLabel: this.serialNumberLabel }));
      }
    );
  }

  getSubjects(targetId: string = null): void {
    this.oscSpinnerService.show();
    this.subjectService.getSubjects().subscribe(
      (subjects) => {
        this.updateSubjects.emit(subjects);

        this.subjects = this.createSubjectWrapper(subjects);
        this.subjectForm.setValue(this.subjects.find((wrapper) => wrapper.subject.id === targetId));
        if (!this.newInspectionTitleUpdatedByUser) this.loadInspectionName();
        this.oscSpinnerService.hide();
      },
      (error) => {
        console.log(error);
        this.oscSpinnerService.hide();
      }
    );
  }

  openCreateSubjectDialog(): Observable<Subject> {
    const dialogRef = this.dialog.open(SettingsSubjectEditDialogComponent, {
      width: "524px",
      maxHeight: "95%",
      autoFocus: false,
      data: {}
    });

    const elements = document.getElementsByClassName("cdk-overlay-pane");
    Array.from(elements).forEach((item: HTMLElement) => {
      item.style.overflowY = "auto";
      item.style.overflowX = "hidden";
    });

    return dialogRef.afterClosed();
  }

  loadInspectionName() {
    if (!this.isEditableName && !this.isUpdateMode && !this.newInspectionTitleUpdatedByUser) {
      let tempName = "";

      if (this.inspectionDateForm.value) {
        const theDate = new Date(this.inspectionDateForm.value);
        const year = theDate.getFullYear().toString();
        const month = ("0" + (theDate.getMonth() + 1)).slice(-2); // 0埋め
        const date = ("0" + theDate.getDate()).slice(-2); // 0埋め
        tempName += year + month + date;
      } else {
        tempName += "yyyymmdd";
      }
      tempName += "-";

      if (this.inspectorsForm.value && this.inspectorsForm.value[0]) {
        tempName += this.inspectorsForm.value[0].name;
      } else {
        tempName += "Inspector Name";
      }
      tempName += "-";

      if (this.subjectForm.value) {
        tempName += this.subjectForm.value.subject.serialNo;
      } else {
        tempName += "SN";
      }
      this.inspectionTitleForm.setValue(tempName);
    }
    if (!this.isUpdateMode && this.subjectForm.value) {
      this.inspectionTitleForm.updateValueAndValidity();
      this.inspectionTitleForm.markAsTouched();
    }
  }

  regexValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }

      return !this.regularExpression.test(control.value) && this.subjectForm.value ? { regex: "false" } : null;
    };
  }

  // Determine whether there are whitespace characters (half-width space/tab/line break/page break) or full-width spaces
  spaceValidator(): boolean {
    if (!this.inspectionTitleForm.value) {
      return null;
    }
    return REGULAR_EXPRESSION.SPACE_PATTERN.test(this.inspectionTitleForm.value);
  }

  // Check if there is a dot "."
  dotValidator(): boolean {
    if (!this.inspectionTitleForm.value) {
      return null;
    }
    return REGULAR_EXPRESSION.DOT_PATTERN.test(this.inspectionTitleForm.value);
  }

  isReadOnly(): boolean {
    return this.inspection && (!this.inspection.permission || (this.inspection.permission & PERMISSION_CODE.WRITE) === 0);
  }

  getPlaceholder(label: string, isSerial: boolean = false, isReadOnly: boolean = false): string {
    if (label === null || label === undefined) {
      label = "";
    }

    let tmpLabel = label;

    if (isReadOnly) {
      tmpLabel = this.serialNumberLabel;
      if (this.subjectForm.disabled || this.isReadOnly()) {
        tmpLabel += " " + this.translate.instant("inspectionUpsertDialog.readonly");
      }
    }
    return tmpLabel;
  }

  subjectFiltering(str: string = ""): void {
    if (!str) {
      // Return the entire amount
      this.filteredSubjects = this.subjects;
      this.updateGroupedSubjects();

      return;
    }
    const filteredSubjectModelIds = this.subjectModels.filter((_) => _.name?.toLowerCase().indexOf(str.toLowerCase()) !== -1).map((_) => _.id);

    this.filteredSubjects = this.subjects.filter(
      (_) =>
        filteredSubjectModelIds.includes(_.subject?.subjectModelId) ||
        this.ignoreCaseCompare(_.subject?.serialNo, str) ||
        this.ignoreCaseCompare(_.subject?.owner, str) ||
        this.ignoreCaseCompare(_.subject?.country, str) ||
        this.ignoreCaseCompare(_.subject?.state, str) ||
        this.ignoreCaseCompare(_.subject?.district, str) ||
        this.ignoreCaseCompare(_.subject?.site, str) ||
        this.ignoreCaseCompare(_.subject?.project, str)
    );
    this.updateGroupedSubjects();
  }

  openChangeSelect(opened: boolean): void {
    const input = this.searchInput.nativeElement;
    if (opened) {
      input.value = "";
      this.subjectFiltering();
    } else {
      // When the selection option is hidden, the selection candidates are reset to the default
      // => Because the specification is that selection items that are not in <mat-option> will not be displayed even if they are selected in <mat-select>
      this.subjectFiltering();
    }
  }

  /** Comparison Functions for Mat-Select
   * @param  {} o1
   * @param  {} o2
   * @returns boolean
   */
  compareWithFn(o1, o2): boolean {
    return o1 && o2 && o1.id === o2.id;
  }

  async downloadStructure() {
    this.dowloadingStructure = true;
    try {
      const blob = await firstValueFrom(this.structureService.getStructureZip(this.inspection.subjectModelId, null, true));
      await this.inspectionTemplateDownloadService.downloadInspectionTemplate(blob, this.inspectionName);
    } catch (error) {
      this.snackBar.open(this.translate.instant("inspectionUpsertDialog.downloadStructureError"));
    } finally {
      if (!this.formsWereModified()) {
        this.dialogRef.close();
      }
      this.dowloadingStructure = false;
    }
  }

  formsWereModified(): boolean {
    let inspectorsWereModified = this.inspectorsForm.dirty;
    if (inspectorsWereModified) {
      const newInspectors = this.inspectorsForm?.value ? (this.inspectorsForm.value as InspectorWrapper[]) : [];
      const oldInspectors = this.inspection.inspectors;
      inspectorsWereModified =
        newInspectors.length !== oldInspectors.length ||
        newInspectors.some((newInspector, index) => newInspector.inspector.id !== oldInspectors[index].id);
    }
    return (
      (this.inspectionTitleForm.dirty && this.inspectionTitleForm?.value !== this.inspection.title) ||
      (this.inspectionDateForm.dirty && this.inspectionDateForm?.value !== this.inspection.inspectionDate) ||
      (this.subjectForm.dirty && this.subjectForm?.value?.subject?.id !== this.inspection.subjectId) ||
      (this.inspectorsForm.dirty && this.inspectorsForm?.value?.length !== this.inspection.inspectors?.length) ||
      inspectorsWereModified
    );
  }

  onKeyUpHandleInspectionName() {
    this.newInspectionTitleUpdatedByUser = true;
  }

  hasSubjectAttributes() {
    if (!this.subjectForm?.value?.subject) {
      return false;
    }

    return (
      (this.isShowOwner && this.subjectForm.value.subject.owner) ||
      (this.isShowCountry && this.subjectForm.value.subject.country) ||
      (this.isShowState && this.subjectForm.value.subject.state) ||
      (this.isShowDistrict && this.subjectForm.value.subject.district) ||
      (this.isShowSite && this.subjectForm.value.subject.site) ||
      (this.isShowProject && this.subjectForm.value.subject.project)
    );
  }

  openLocationVersionConfirmDialog() {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: "500px",
      height: "280px",
      data: {
        firstMessage: this.translate.instant("inspectionUpsertDialog.message1"),
        secondMessage: this.translate.instant("inspectionUpsertDialog.message2"),
        trueBtnText: this.translate.instant("inspectionUpsertDialog.yes"),
        falseBtnText: this.translate.instant("inspectionUpsertDialog.no"),
        dialogType: "confirmation"
      }
    });
  }

  getTemplateVersion(): number {
    if (this.isUpdateMode) {
      return this.inspection.locationVersion;
    } else {
      const model = this.subjectModels.find((obj) => this.subjectForm?.value?.subject?.subjectModelId === obj.id);
      return model ? model.version : null;
    }
  }

  getAttributesVersion(): number {
    if (this.isUpdateMode) {
      return this.inspection.attributesVersion;
    } else {
      const subject = this.subjects.find((obj) => this.subjectForm?.value?.subject?.id === obj.subject.id);
      return subject ? subject.subject.version : null;
    }
  }

  sendDataForIplexDevice() {
    const titleSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\TITLE.dat`;
    const marSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\MARK_SET.dat`;
    const inspectionFolderName = this.utilsService.trimAllSpace(this.inspection.title);
    let subjectModelSchema: SubjectModelSchema = null;

    this.oscSpinnerService.show(true);
    const createTitleFileOb = this.titleDBService
      .getAll()
      .pipe(switchMap((schema) => this.fileSystemAccessService.createTextFile(this.offlineUtils.generateTitlesText(schema), titleSrcPath)));

    const createMarkFileOb = this.marksStateService.markItems$.pipe(
      switchMap((markItems) => this.fileSystemAccessService.createTextFile(this.offlineUtils.generateMarksText(markItems), marSrcPath))
    );

    zip(this.subjectModelDbService.getOne(this.inspection.subjectModelId), createTitleFileOb, createMarkFileOb)
      .pipe(
        switchMap((results) => {
          subjectModelSchema = results[0];
          const isTitleFileSuccess = results[1];
          const isMarkFileSuccess = results[2];

          if (!isTitleFileSuccess || !isMarkFileSuccess) {
            throw new ApplicationError("Title/Mark data is invalid");
          }

          return this.iplexDeviceSyncService.onWebDAVConnectMode();
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (!isSuccess) {
            return throwError("Failed link with IPLEX G.");
          }

          return this.postDatFiles();
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (isSuccess.some((_) => _ === false)) {
            throwError({ type: "postDatFiles" });
          }

          let postInspectionFolderOb: Observable<boolean> = of(false);
          if (OFFLINE_REGEX.File_Stat_Basename.test(inspectionFolderName)) {
            postInspectionFolderOb = this.iplexDeviceSyncService.postDirectoryInSDcard(`/${inspectionFolderName}`);
          }

          return postInspectionFolderOb;
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (!isSuccess) {
            throwError({ type: "failedInspectionFolder" });
          }
          const subjectModelLocation = subjectModelSchema.subjectModelLocations.find((_) => _.version === this.inspection.locationVersion);
          const locations = JSON.parse(subjectModelLocation.locations) as SubjectLocation[];
          const filteredLocations = locations.filter((location) => OFFLINE_REGEX.File_Stat_Basename.test(location.identifier));

          return this.postSubjectLocationFolders(inspectionFolderName, filteredLocations);
        })
      )
      .pipe(
        switchMap((isSuccess: boolean[]) => {
          if (isSuccess.some((_) => _ === false)) {
            throwError({ type: "failedInspectionFolder" });
          }
          return this.iplexDeviceSyncService.stopSdCardWebDAV();
        })
      )
      .subscribe(
        (_) => {
          this.snackBar.open(this.translate.instant("inspectionUpsertDialog.sendSuccess"));
          this.oscSpinnerService.hide(true);
        },
        (error) => {
          console.log(error);
          if (error?.type === "postDatFiles") {
            this.snackBar.open(
              this.translate.instant("inspectionUpsertDialog.sendDatFilesFailed", {
                lowerCaseMarkLabel: this.lowerCaseMarkLabel,
                lowerCaseTitleLabel: this.lowerCaseTitleLabel
              })
            );
          } else if (error?.type === "failedInspectionFolder") {
            this.snackBar.open(this.translate.instant("inspectionUpsertDialog.sendInspectionFolderFailed"));
          } else {
            this.snackBar.open(this.translate.instant("inspectionUpsertDialog.sendFailed"));
          }
          this.oscSpinnerService.hide(true);
          this.iplexDeviceSyncService.offWebDAVConnectMode().subscribe((_) => _);
        }
      );
  }

  postDatFiles() {
    const presetFolderPath = `/${FILE_SYSTEM.PATH.PRESET}`;
    const titleSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\TITLE.dat`;
    const titleDestPath = `/${FILE_SYSTEM.PATH.PRESET}/TITLE.dat`;
    const marSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\MARK_SET.dat`;
    const marDestPath = `/${FILE_SYSTEM.PATH.PRESET}/MARK_SET.dat`;

    return this.iplexDeviceSyncService.postDirectoryInSDcard(presetFolderPath).pipe(
      switchMap((isSuccess) => {
        return isSuccess
          ? zip(
              this.iplexDeviceSyncService.postFileForSDcard(titleSrcPath, titleDestPath),
              this.iplexDeviceSyncService.postFileForSDcard(marSrcPath, marDestPath)
            )
          : of([false, false]);
      })
    );
  }

  postSubjectLocationFolders(inspectionName: string, locations: SubjectLocation[]) {
    this.folderIndex = 1;
    const folderList: string[] = [];
    this.invalidLocationFolderNameList = [];
    locations.forEach((location) => this.addLocationFolder(location, folderList));

    const obs = folderList
      .map((folderName) => `/${inspectionName}/${folderName}`)
      .map((folderPath) => this.iplexDeviceSyncService.postDirectoryInSDcard(folderPath));

    return zip(...obs);
  }

  /** Show an error dialog
   * @returns void
   */
  openFolderInUseDialog(): void {
    const dialogRef = this.dialog.open(CommonFolderInUseDialogComponent, {
      minWidth: "700px",
      height: "230px",
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.update();
      }
    });
  }

  private ignoreCaseCompare(targetStr: string, searchStr?: string): boolean {
    if (!targetStr) {
      return false;
    }
    return targetStr.toLowerCase().indexOf(searchStr.toLowerCase()) !== -1;
  }

  private addLocationFolder(location: SubjectLocation, folderList: string[]) {
    if (location.children.length > 0) {
      location.children.forEach((child) => {
        this.addLocationFolder(child, folderList);
      });
    } else {
      if (location.isDataPlate) {
        const folderName = "00_" + location.identifier;
        if (OFFLINE_REGEX.File_Stat_Basename.test(folderName)) {
          folderList.push(folderName);
        } else {
          this.invalidLocationFolderNameList.push(location.identifier);
        }
      } else {
        const folderName = String(this.folderIndex).padStart(2, "0") + "_" + location.identifier;
        if (OFFLINE_REGEX.File_Stat_Basename.test(folderName)) {
          folderList.push(folderName);
        } else {
          this.invalidLocationFolderNameList.push(location.identifier);
        }
        this.folderIndex++;
      }
    }

    return folderList;
  }
}
